import { Node, mergeAttributes } from '@tiptap/core';

// K.3 — węzły TipTap dla konstruktora umów (HYBRYDA).
// Zmienna = pigułka (jak @wzmianka), tabela sprzętu = chroniony blok.
// KLUCZOWE: serializacja (renderHTML/getHTML) zachowuje round-trip do backendu:
//   - VariablePill   → <span data-variable="name">{{name}}</span>  (replaceVariables na backendzie podstawia {{name}})
//   - EquipmentBlock → <div data-equipment-table="true"></div>     (backend wstawia tu tabelę sprzętu)
// NodeView (widok w edytorze) pokazuje przyjazną etykietę / placeholder — to tylko warstwa wizualna.

// Pigułka zmiennej — inline, atomowy (jeden niepodzielny węzeł), nieedytowalny w środku.
export const VariablePill = Node.create({
  name: 'variablePill',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      // Mapa name → przyjazna etykieta (np. 'firstName' → 'Imię klienta'). Domyślnie sam kod.
      labelFor: (name) => `{{${name}}}`,
    };
  },

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-variable') || '',
        renderHTML: (attrs) => (attrs.name ? { 'data-variable': attrs.name } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },

  // Serializacja do HTML (getHTML → magazyn → backend). Tekst {{name}} w środku
  // pozwala istniejącemu replaceVariables na backendzie podstawić wartość.
  renderHTML({ HTMLAttributes, node }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'tt-variable' }), `{{${node.attrs.name}}}`];
  },

  // Serializacja do czystego tekstu (np. kopiowanie) — zachowuje token.
  renderText({ node }) {
    return `{{${node.attrs.name}}}`;
  },

  // Widok w edytorze: przyjazna etykieta zamiast surowego {{name}}.
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('span');
      dom.className = 'tt-variable';
      dom.setAttribute('data-variable', node.attrs.name);
      dom.setAttribute('contenteditable', 'false');
      dom.textContent = this.options.labelFor(node.attrs.name);
      return { dom };
    };
  },
});

// Chroniony blok tabeli sprzętu — blokowy, atomowy, nieedytowalny.
// W edytorze: placeholder „Tabela sprzętu". W magazynie: pusty <div data-equipment-table>,
// który backend rozpoznaje i podmienia na wygenerowaną tabelę.
export const EquipmentBlock = Node.create({
  name: 'equipmentBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      // Placeholder w edytorze — nadpisywany z SectionEditor.js przez t().
      placeholder: 'Tabela sprzętu (wypełni się automatycznie danymi rezerwacji)',
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-equipment-table]' }];
  },

  renderHTML() {
    return ['div', { 'data-equipment-table': 'true' }];
  },

  renderText() {
    return '{{equipmentTable}}';
  },

  addNodeView() {
    return () => {
      const dom = document.createElement('div');
      dom.className = 'tt-equipment-block';
      dom.setAttribute('contenteditable', 'false');
      dom.textContent = this.options.placeholder;
      return { dom };
    };
  },
});

// Chroniony blok logo firmy — blokowy, atomowy. W edytorze placeholder; w magazynie
// pusty <div data-company-logo>, który backend podmienia na <img> z `company_logo`.
export const LogoBlock = Node.create({
  name: 'logoBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      // Placeholder w edytorze — nadpisywany z SectionEditor.js przez t().
      placeholder: 'Logo firmy (wstawi się logo z „Dane firmy")',
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-company-logo]' }];
  },

  // mergeAttributes przepuszcza globalny atrybut wyrównania (TextAlign → style="text-align"),
  // dzięki czemu pasek edytora wyrównuje logo w miejscu wstawienia.
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-company-logo': 'true' })];
  },

  renderText() {
    return '{{companyLogo}}';
  },

  addNodeView() {
    return () => {
      const dom = document.createElement('div');
      dom.className = 'tt-logo-block';
      dom.setAttribute('contenteditable', 'false');
      dom.textContent = this.options.placeholder;
      return { dom };
    };
  },
});

// Escape dla treści legacy (czysty string) zanim potraktujemy ją jako HTML.
// & musi być pierwsze.
function escapeHtmlClient(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Konwersja treści sekcji → HTML wejściowy dla TipTap.
// Rozróżnienie po FLADZE `format` (to jest właściwy dyskryminator, nie zgadywanie tagów):
//   - format==='html' (lub treść ma NASZE markery data-variable/data-equipment-table) → to już
//     HTML edytora, przepuść bez zmian;
//   - inaczej (legacy plain string: {{var}}, \n) → zbuduj HTML (pigułki, blok sprzętu, <br>) z
//     pełnym escapowaniem (treść legacy z dosłownym <p>/<a> nie zostanie potraktowana jak HTML).
// Dzięki temu stare opublikowane szablony otwierają się poprawnie, a zapis (getHTML) jest stabilny.
export function contentToEditorHtml(content, format) {
  const str = String(content == null ? '' : content);
  const hasOurMarkers = /data-variable|data-equipment-table|data-company-logo/i.test(str);
  if (format === 'html' || hasOurMarkers) return str;

  // Legacy: escapuj, podmień tokeny na elementy, \n → <br>.
  let html = escapeHtmlClient(str)
    .replace(/\{\{\s*equipmentTable\s*\}\}/g, '<div data-equipment-table="true"></div>')
    .replace(/\{\{\s*companyLogo\s*\}\}/g, '<div data-company-logo="true"></div>')
    .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => `<span data-variable="${name}">{{${name}}}</span>`)
    .replace(/\r?\n/g, '<br>');
  return html;
}
